import { useCallback, useState } from "react";
import {
  Box,
  Heading,
  Badge,
  HStack,
  Text,
  Image,
  Button,
} from "@chakra-ui/react";
import Card from "@/src/components/card/Card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Get, Post } from "@/src/api";
import { useToast } from "@/src/hooks/use-toast";
import LoadingIndicator from "@/src/components/loading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/src/components/ui/table";
import { XIcon } from "lucide-react";
import useAdmin from "@/src/hooks/useAdmin";
import { Redirect } from "react-router-dom";

export interface SocialMediaPost {
  id: number;
  user_id: number;
  status: string;
  social_media: string;
  posted_at: string;
  link: string;
}

const getSocialMediaBadgeColor = (platform: string) => {
  switch (platform.toLowerCase()) {
    case "instagram":
      return "purple";
    case "facebook":
      return "blue";
    case "reddit":
      return "orange";
    case "discord":
      return "purple";
    case "x (twitter)":
    case "twitter":
      return "cyan";
    case "threads":
      return "gray";
    case "snapchat":
      return "yellow";
    case "pinterest":
      return "red";
    default:
      return "gray";
  }
};

const getStatusBadgeColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "checking":
      return "orange";
    case "approved":
      return "green";
    case "rejected":
      return "red";
    default:
      return "gray";
  }
};

const formatDate = (dateString: string) => {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateString));
};

export default function SocialMediaPostsPage() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isAdmin = useAdmin();

  const { data, isLoading } = useQuery<SocialMediaPost[]>({
    queryKey: ["socialMediaPosts"],
    queryFn: async () => {
      return Get("/support/social-media-posts");
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (postId: number) => {
      return Post(`/support/social-media-posts/${postId}/approve`, {});
    },
    onSuccess: (data, postId) => {
      queryClient.invalidateQueries({ queryKey: ["socialMediaPosts"] });
      toast({
        title: "Success",
        description: `Post #${postId} has been approved successfully.`,
        variant: "default",
      });
    },
    onError: (error: Error, postId) => {
      toast({
        title: "Error",
        description:
          error.message ||
          `Failed to approve post #${postId}. Please try again.`,
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (postId: number) => {
      return Post(`/support/social-media-posts/${postId}/reject`, {});
    },
    onSuccess: (data, postId) => {
      queryClient.invalidateQueries({ queryKey: ["socialMediaPosts"] });
      toast({
        title: "Success",
        description: `Post #${postId} has been rejected successfully.`,
        variant: "default",
      });
    },
    onError: (error: Error, postId) => {
      toast({
        title: "Error",
        description:
          error.message ||
          `Failed to reject post #${postId}. Please try again.`,
        variant: "destructive",
      });
    },
  });

  const handleApprove = useCallback(
    async (postId: number) => {
      approveMutation.mutate(postId);
    },
    [approveMutation],
  );

  const handleReject = useCallback(
    async (postId: number) => {
      rejectMutation.mutate(postId);
    },
    [rejectMutation],
  );

  const handleUserClick = (userId: number) => {
    window.open(`/users?userId=${userId}`, "_blank");
  };

  if (!isAdmin) {
    return <Redirect to={"/"} />;
  }

  if (isLoading) return <LoadingIndicator />;

  return (
    <>
      <Card>
        <Box p={6}>
          <HStack justify="space-between" mb={6}>
            <Heading size="lg" color="gray.700">
              Social Media Posts
            </Heading>
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
              {data?.length || 0} Posts
            </Badge>
          </HStack>

          <Box
            overflowX="auto"
            bg="white"
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-700">
                    Post ID
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700">
                    User ID
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700">
                    Platform
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700">
                    Status
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700">
                    Posted At
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700 text-center">
                    Image
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700 text-center">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.map((post: SocialMediaPost) => (
                  <TableRow key={post.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">#{post.id}</TableCell>
                    <TableCell>
                      <Text
                        color="blue.600"
                        cursor="pointer"
                        _hover={{ textDecoration: "underline" }}
                        onClick={() => handleUserClick(post.user_id)}
                      >
                        #{post.user_id}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Badge
                        colorScheme={getSocialMediaBadgeColor(
                          post.social_media,
                        )}
                        px={2}
                        py={1}
                        borderRadius="md"
                      >
                        {post.social_media}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        colorScheme={getStatusBadgeColor(post.status)}
                        px={2}
                        py={1}
                        borderRadius="md"
                      >
                        {post.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Text fontSize="sm" color="gray.600">
                        {formatDate(post.posted_at)}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="center">
                        <Image
                          src={post.link}
                          alt={`${post.social_media} post`}
                          w="54px"
                          h="96px"
                          objectFit="cover"
                          objectPosition="center"
                          borderRadius="md"
                          cursor="pointer"
                          border="1px"
                          borderColor="gray.200"
                          _hover={{ opacity: 0.8 }}
                          onClick={() => setSelectedImage(post.link)}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <HStack spacing={2} justify="center">
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={() => handleApprove(post.id)}
                          isLoading={approveMutation.isPending}
                          isDisabled={
                            rejectMutation.isPending ||
                            post.status !== "checking"
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleReject(post.id)}
                          isLoading={rejectMutation.isPending}
                          isDisabled={
                            approveMutation.isPending ||
                            post.status !== "checking"
                          }
                        >
                          Reject
                        </Button>
                      </HStack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {data?.length === 0 && (
            <Box textAlign="center" py={12}>
              <Text fontSize="lg" color="gray.500">
                No social media posts found
              </Text>
            </Box>
          )}
        </Box>
      </Card>

      {selectedImage && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.800"
          zIndex="9999"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() => setSelectedImage(null)}
          cursor="pointer"
        >
          <Box maxW="90vw" maxH="90vh" position="relative">
            <Image
              src={selectedImage}
              alt="Full size preview"
              maxW="100%"
              maxH="90vh"
              objectFit="contain"
              borderRadius="lg"
            />
            <Button
              position="absolute"
              top="2"
              right="2"
              colorScheme="gray"
              variant="outline"
              onClick={() => setSelectedImage(null)}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}
